import React, { useEffect, useState } from "react";
import { useAuth } from "../../context/AuthContext";
import { useData } from "../../context/DataContext";
import { useNavigation } from "../../context/NavigationContext";
import { getMyAttendance } from "../../api/attendance";
import { createLeaveRequest, cancelLeave } from "../../api/leave";
import { createPermitRequest, getPermitBalance, cancelPermit } from "../../api/permit";
import { getCompensatoryBalance } from "../../api/compensatory";
import { uploadAttachments } from "../../api/attachments";
import { getRequestStatusLabel, isOpenRequest } from "../../utils/requestHelpers";
import {
  findLeaveCoveringPermitDate,
  findOverlappingLeaveRequest,
  findOverlappingPermitRequest,
  findPermitCoveringLeaveRange,
  LEAVE_ON_PERMIT_MESSAGE,
  OVERLAPPING_LEAVE_MESSAGE,
  OVERLAPPING_PERMIT_MESSAGE,
  PERMIT_ON_LEAVE_MESSAGE,
} from "../../utils/leaveOverlap";
import {
  ANNUAL_LEAVE_NOTICE_MESSAGE,
  earliestAnnualLeaveStartDate,
  isAnnualLeaveNoticeSatisfied,
} from "../../utils/leaveNotice";
import { todayDateOnlyIso } from "../../utils/currentLeaveHelpers";
import {
  findWeeklyRestConflictInLeaveRange,
  weeklyRestConflictMessage,
  weeklyRestDayLabelAr,
} from "../../utils/leaveWeeklyRest";
import {
  defaultPermitDateIso,
  findLateDayHint,
  formatLateDayChipLabel,
  listUncoveredLateDays,
} from "../../utils/permitLateDayHints";
import {
  findAbsentDayHint,
  formatAbsentDayChipLabel,
  listUncoveredAbsentDays,
  shouldWarnLeaveDateWithoutAbsence,
} from "../../utils/leaveAbsenceDayHints";
import { calcHoursDiff } from "../../utils/permitHours";
import { buildResubmitDraft } from "../../utils/requestTransparency";
import AttachmentUploader from "../common/AttachmentUploader";
import AttachmentList from "../common/AttachmentList";
import RequestTransparency from "../common/RequestTransparency";
import "./LeaveRequestForm.css";

const DEFAULT_PERMIT_FREE_HOURS = 2;
const DEFAULT_PERMIT_PAID_CAP_HOURS = 2;
const DEFAULT_PERMIT_DEDUCTION_MULTIPLIER = 1.5;
const COMPENSATORY_HOURS_PER_FULL_DAY = 8;
const COMPENSATORY_HOURS_PER_HALF_DAY = 4;
const UNPAID_ANNUAL_LIMIT = 12;

const leaveTypes = [
  { value: "annual", label: "إجازة اعتيادية" },
  { value: "sick", label: "إجازة مرضية" },
  { value: "casual", label: "إجازة عارضة" },
  {
    value: "compensatory",
    label: "إجازة تعويضية",
    hint: "من رصيد الساعات الإضافية — 8 ساعات = يوم كامل، 4 ساعات = نصف يوم بدون خصم من الراتب",
  },
  {
    value: "unpaid",
    label: "إجازة على الحساب الخاص",
    hint: `تُخصم من الراتب يوم بيوم — بحد أقصى ${UNPAID_ANNUAL_LIMIT} يوم في السنة بدون انتظار 6 أشهر`,
  },
];

const permitTypes = [
  { value: "Permit", label: "إذن" },
  {
    value: "CompensatoryPermit",
    label: "إذن تعويضي",
    hint: "من رصيد ساعات الزيادة — بدون خصم من الراتب، صالح لنفس الشهر فقط",
  },
];

/**
 * خصم الإذن حسب الرصيد الشهري المستخدم مسبقاً وقواعد اللائحة:
 * أول freeHours مجاناً، ثم paidCapHours بخصم deductionMultiplier× لكل ساعة.
 */
function calcPermitDeductedHours(
  hours,
  previouslyUsedHours = 0,
  rules = {
    freeHours: DEFAULT_PERMIT_FREE_HOURS,
    paidCapHours: DEFAULT_PERMIT_PAID_CAP_HOURS,
    deductionMultiplier: DEFAULT_PERMIT_DEDUCTION_MULTIPLIER,
  },
) {
  if (!hours || hours <= 0) return 0;
  const used = Math.max(0, Number(previouslyUsedHours) || 0);
  const freeHours = Number(rules.freeHours);
  const paidCapHours = Number(rules.paidCapHours);
  const multiplier = Number(rules.deductionMultiplier);
  const paidStart = Number.isFinite(freeHours)
    ? freeHours
    : DEFAULT_PERMIT_FREE_HOURS;
  const paidEnd =
    paidStart +
    (Number.isFinite(paidCapHours)
      ? paidCapHours
      : DEFAULT_PERMIT_PAID_CAP_HOURS);
  const requestStart = used;
  const requestEnd = used + hours;
  const overlapStart = Math.max(requestStart, paidStart);
  const overlapEnd = Math.min(requestEnd, paidEnd);
  const chargeable = Math.max(0, overlapEnd - overlapStart);
  const factor = Number.isFinite(multiplier)
    ? multiplier
    : DEFAULT_PERMIT_DEDUCTION_MULTIPLIER;
  return Math.round(chargeable * factor * 100) / 100;
}

function formatPermitHoursAr(value) {
  const num = Number(value);
  if (!Number.isFinite(num)) return String(value);
  if (num === 1) return "ساعة";
  if (num === 2) return "ساعتين";
  if (num > 2 && num < 11) return `${num} ساعات`;
  return `${num} ساعة`;
}

function formatPermitDeductionMultiplierAr(value) {
  const num = Number(value);
  if (num === 1) return "ساعة بساعة";
  if (num === 1.5) return "ساعة ونصف";
  if (num === 2) return "ساعتين";
  if (Number.isFinite(num) && num > 0) return `×${num}`;
  return "ساعة ونصف";
}

function compensatoryHoursForLeaveDays(days) {
  const value = Number(days);
  if (!Number.isFinite(value) || value <= 0) return 0;
  if (Math.abs(value - 0.5) < 1e-9) return COMPENSATORY_HOURS_PER_HALF_DAY;
  return Math.round(value * COMPENSATORY_HOURS_PER_FULL_DAY * 100) / 100;
}

function LeaveRequestForm() {
  const { user } = useAuth();
  const { pendingRequests, addPendingRequest, addRecentActivity } = useData();
  const navigation = useNavigation();

  const [leaveType, setLeaveType] = useState("");
  const [days, setDays] = useState("");
  const [durationMode, setDurationMode] = useState("full"); // full | half
  const [halfDayPeriod, setHalfDayPeriod] = useState(""); // FIRST_HALF | SECOND_HALF
  const [permitStartTime, setPermitStartTime] = useState("");
  const [permitEndTime, setPermitEndTime] = useState("");
  const [startDate, setStartDate] = useState("");
  const [reason, setReason] = useState("");
  const [files, setFiles] = useState([]);
  const [createdEntity, setCreatedEntity] = useState(null);
  const [listRefresh, setListRefresh] = useState(0);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [permitBalance, setPermitBalance] = useState(null);
  const [compensatoryBalance, setCompensatoryBalance] = useState(null);
  const [attendanceRecords, setAttendanceRecords] = useState([]);
  const [attendanceHintsLoading, setAttendanceHintsLoading] = useState(false);

  const isCompensatoryPermit = leaveType === "CompensatoryPermit";
  const isCompensatoryLeave = leaveType === "compensatory";
  const isCompensatory = isCompensatoryPermit || isCompensatoryLeave;
  const isPermit = leaveType === "Permit" || isCompensatoryPermit;
  const isLeaveRequestType = Boolean(leaveType) && !isPermit;
  const wantsAttendanceHints = isPermit || isLeaveRequestType;

  function isOwnRequest(req) {
    if (user?.id && req.userId) return req.userId === user.id;
    // Fallback only when both codes are non-empty (avoid "" === "" / undefined === undefined)
    const code = user?.code;
    return Boolean(code && req.employeeCode && req.employeeCode === code);
  }

  const ownPendingRequests = pendingRequests.filter(isOwnRequest);
  const lateDays = isPermit
    ? listUncoveredLateDays(attendanceRecords, ownPendingRequests)
    : [];
  const absentDays = isLeaveRequestType
    ? listUncoveredAbsentDays(attendanceRecords, ownPendingRequests)
    : [];
  const selectedLateDay = isPermit ? findLateDayHint(lateDays, startDate) : null;
  const selectedAbsentDay = isLeaveRequestType
    ? findAbsentDayHint(absentDays, startDate)
    : null;
  const attendanceMonthKey = wantsAttendanceHints
    ? (startDate || todayDateOnlyIso()).slice(0, 7)
    : "";
  const todayIso = todayDateOnlyIso();
  const showLeaveAbsenceWarning =
    isLeaveRequestType &&
    startDate &&
    !attendanceHintsLoading &&
    !selectedAbsentDay &&
    shouldWarnLeaveDateWithoutAbsence(startDate, todayIso);
  const leaveSpanDays =
    isLeaveRequestType && durationMode === "half"
      ? 0.5
      : isLeaveRequestType
        ? Number(days || 0)
        : 0;
  const leaveWeeklyRestConflict =
    isLeaveRequestType && startDate && leaveSpanDays > 0
      ? findWeeklyRestConflictInLeaveRange({
          startDate,
          days: leaveSpanDays,
          weeklyRestDay: user?.weeklyRestDay,
        })
      : null;

  // حساب ساعات الإذن تلقائياً من الوقتين
  const computedHours = isPermit
    ? calcHoursDiff(permitStartTime, permitEndTime)
    : null;
  const usedPermitHours = Number(permitBalance?.usedHours ?? 0);
  const permitFreeHours = Number(
    permitBalance?.freeHours ?? DEFAULT_PERMIT_FREE_HOURS,
  );
  const permitPaidCapHours = Number(
    permitBalance?.paidCapHours ?? DEFAULT_PERMIT_PAID_CAP_HOURS,
  );
  const permitDeductionMultiplier = Number(
    permitBalance?.deductionMultiplier ?? DEFAULT_PERMIT_DEDUCTION_MULTIPLIER,
  );
  const permitMaxMonthlyHours = Number(
    permitBalance?.maxMonthlyHours ??
      permitFreeHours + permitPaidCapHours,
  );
  const remainingPermitHours = Number(
    permitBalance?.remainingHours ?? permitMaxMonthlyHours,
  );
  const remainingCompensatoryHours = Number(
    compensatoryBalance?.remaining ?? 0,
  );
  const requestedCompensatoryLeaveHours = isCompensatoryLeave
    ? compensatoryHoursForLeaveDays(
        durationMode === "half" ? 0.5 : Number(days || 0),
      )
    : 0;
  const deductedHours =
    isPermit && !isCompensatoryPermit
      ? calcPermitDeductedHours(computedHours, usedPermitHours, {
          freeHours: permitFreeHours,
          paidCapHours: permitPaidCapHours,
          deductionMultiplier: permitDeductionMultiplier,
        })
      : isCompensatoryPermit
        ? 0
        : null;

  useEffect(() => {
    if (!isPermit && !isCompensatoryLeave) {
      setPermitBalance(null);
      setCompensatoryBalance(null);
      return undefined;
    }

    let cancelled = false;
    const dateParam = startDate || undefined;

    if (isCompensatory) {
      setPermitBalance(null);
      getCompensatoryBalance({})
        .then((balance) => {
          if (!cancelled) setCompensatoryBalance(balance);
        })
        .catch(() => {
          if (!cancelled) setCompensatoryBalance(null);
        });
    } else {
      setCompensatoryBalance(null);
      getPermitBalance(dateParam ? { date: dateParam } : {})
        .then((balance) => {
          if (!cancelled) setPermitBalance(balance);
        })
        .catch(() => {
          if (!cancelled) setPermitBalance(null);
        });
    }

    return () => {
      cancelled = true;
    };
  }, [isPermit, isCompensatoryLeave, isCompensatory, startDate, success]);

  useEffect(() => {
    if (!wantsAttendanceHints || !attendanceMonthKey) {
      setAttendanceRecords([]);
      setAttendanceHintsLoading(false);
      return undefined;
    }

    let cancelled = false;
    const today = todayDateOnlyIso();
    const [yearText, monthText] = attendanceMonthKey.split("-");
    const year = Number(yearText);
    const month = Number(monthText);
    if (!year || !month) {
      setAttendanceRecords([]);
      return undefined;
    }

    setAttendanceHintsLoading(true);
    getMyAttendance({ month, year })
      .then((data) => {
        if (cancelled) return;
        const records = data?.records || [];
        setAttendanceRecords(records);
        if (isPermit) {
          const days = listUncoveredLateDays(records, ownPendingRequests);
          setStartDate((current) => current || defaultPermitDateIso(days, today));
        }
      })
      .catch(() => {
        if (cancelled) return;
        setAttendanceRecords([]);
        if (isPermit) {
          setStartDate((current) => current || today);
        }
      })
      .finally(() => {
        if (!cancelled) setAttendanceHintsLoading(false);
      });

    return () => {
      cancelled = true;
    };
    // ownPendingRequests is sampled on fetch; chips recompute on render.
  }, [wantsAttendanceHints, attendanceMonthKey, isPermit, success]);

  const existingPendingLeave = pendingRequests.find(
    (req) =>
      isOwnRequest(req) &&
      (req._source === "leave" ||
        (req.category === "leave" && req._source !== "permit")) &&
      isOpenRequest(req),
  );

  const existingPendingPermit = pendingRequests.find(
    (req) =>
      isOwnRequest(req) &&
      (req._source === "permit" ||
        req.category === "permit" ||
        req.type === "إذن" ||
        req.type === "إذن تعويضي") &&
      isOpenRequest(req),
  );

  const lastRejectedLeaveOrPermit = [...pendingRequests]
    .filter(
      (req) =>
        isOwnRequest(req) &&
        req.status === "rejected" &&
        (req._source === "leave" ||
          req._source === "permit" ||
          req.category === "permit" ||
          req.type === "إذن" ||
          req.type === "إذن تعويضي" ||
          String(req.type || "").includes("إجازة")),
    )
    .sort((a, b) => String(b.submittedAt || "").localeCompare(String(a.submittedAt || "")))[0];

  function applyLeaveDraft(draft) {
    if (!draft) return;
    if (draft.kind === "permit") {
      setLeaveType(draft.leaveType || "Permit");
      setStartDate(draft.startDate || "");
      setPermitStartTime(draft.permitStartTime || "");
      setPermitEndTime(draft.permitEndTime || "");
      setReason(draft.reason || "");
      setDurationMode("full");
      setDays("");
      setHalfDayPeriod("");
      return;
    }
    setLeaveType(draft.leaveType || "annual");
    setStartDate(draft.startDate || "");
    setReason(draft.reason || "");
    setPermitStartTime("");
    setPermitEndTime("");
    if (draft.halfDayPeriod || Number(draft.days) === 0.5) {
      setDurationMode("half");
      setDays("0.5");
      setHalfDayPeriod(draft.halfDayPeriod || "");
    } else {
      setDurationMode("full");
      setDays(draft.days || "");
      setHalfDayPeriod("");
    }
  }

  useEffect(() => {
    const draft = navigation?.requestDraft;
    if (!draft || (draft.kind && draft.kind !== "leave" && draft.kind !== "permit")) {
      return undefined;
    }
    applyLeaveDraft(draft);
    navigation.clearRequestDraft?.();
    return undefined;
  }, [navigation?.requestDraft]);

  function handleLeaveTypeChange(value) {
    setLeaveType(value);
    setDays("");
    setDurationMode("full");
    setHalfDayPeriod("");
    setPermitStartTime("");
    setPermitEndTime("");
    setStartDate("");
  }

  async function uploadAfterCreate(entityType, entityId) {
    if (!files.length || !entityId) return;
    await uploadAttachments(entityType, entityId, files);
    setFiles([]);
    setCreatedEntity({ type: entityType, id: entityId });
    setListRefresh((n) => n + 1);
  }

  async function handleSubmitLeave(e) {
    e.preventDefault();
    if (submitting) return;
    setError("");
    setSuccess(false);

    if (isPermit) {
      if (existingPendingPermit) {
        setError("لا يمكن تقديم طلب إذن جديد قبل الرد على طلبك الحالي");
        return;
      }

      if (!permitStartTime || !permitEndTime) {
        setError("من فضلك أدخل وقت بداية ونهاية الإذن");
        return;
      }

      if (!computedHours || computedHours <= 0) {
        setError("وقت النهاية يجب أن يكون بعد وقت البداية");
        return;
      }

      if (Math.abs(computedHours * 2 - Math.round(computedHours * 2)) > 1e-9) {
        setError("مدة الإذن يجب أن تكون بنصف ساعة أو مضاعفاتها (0.5، 1، 1.5، …)");
        return;
      }

      if (!startDate) {
        setError("من فضلك أدخل تاريخ الإذن");
        return;
      }

      if (isCompensatory) {
        const now = new Date();
        const y = Number(startDate.slice(0, 4));
        const m = Number(startDate.slice(5, 7));
        if (
          y !== now.getFullYear() ||
          m !== now.getMonth() + 1
        ) {
          setError(
            "الإذن التعويضي يُصرف في نفس الشهر الميلادي فقط ولا يُرحَّل لشهر آخر",
          );
          return;
        }

        if (compensatoryBalance && remainingCompensatoryHours <= 0) {
          setError(
            "لا يوجد رصيد إذن تعويضي متاح هذا الشهر (أو انتهت صلاحيته).",
          );
          return;
        }

        if (
          compensatoryBalance &&
          computedHours > remainingCompensatoryHours
        ) {
          setError(
            `المتبقي من رصيد الإذن التعويضي هذا الشهر ${remainingCompensatoryHours} ساعة فقط.`,
          );
          return;
        }
      } else {
        if (permitBalance && remainingPermitHours <= 0) {
          setError(
            `تم استنفاد رصيد الأذونات لهذا الشهر (${permitMaxMonthlyHours} ساعات). لا يمكن تقديم إذن جديد، وسيتم الخصم مباشرة من الراتب في حالة التأخير.`,
          );
          return;
        }

        if (
          permitBalance &&
          computedHours > remainingPermitHours
        ) {
          setError(
            `المتبقي من رصيد الأذونات هذا الشهر ${remainingPermitHours} ساعة فقط (الحد الأقصى ${permitMaxMonthlyHours} ساعات شهرياً).`,
          );
          return;
        }
      }

      const overlappingPermit = findOverlappingPermitRequest(
        pendingRequests,
        {
          startDate,
          permitDate: startDate,
          startTime: permitStartTime,
          endTime: permitEndTime,
          permitStartTime,
          permitEndTime,
        },
        isOwnRequest,
      );
      if (overlappingPermit) {
        setError(OVERLAPPING_PERMIT_MESSAGE);
        return;
      }

      const coveringLeave = findLeaveCoveringPermitDate(
        pendingRequests,
        startDate,
        isOwnRequest,
      );
      if (coveringLeave) {
        setError(PERMIT_ON_LEAVE_MESSAGE);
        return;
      }

      setSubmitting(true);
      try {
        const created = await createPermitRequest({
          permitDate: startDate,
          startTime: permitStartTime,
          endTime: permitEndTime,
          reason: reason.trim() || undefined,
          isCompensatory: isCompensatory || undefined,
        });

        await uploadAfterCreate("permission", created?.id);

        await addPendingRequest();

        addRecentActivity({
          id: Date.now(),
          text: `طلب ${isCompensatory ? "إذن تعويضي" : "إذن"} من ${user?.name || "موظف"}`,
          date: new Date().toLocaleDateString("ar-EG"),
          type: "permit",
        });

        setSuccess(true);
      } catch (err) {
        const message = err.response?.data?.message;
        setError(
          Array.isArray(message)
            ? message.join("\n")
            : message || "تعذر إرسال طلب الإذن",
        );
      } finally {
        setSubmitting(false);
      }
      return;
    }

    if (existingPendingLeave) {
      setError("لا يمكن تقديم طلب جديد قبل الرد على طلبك الحالي");
      return;
    }

    if (!leaveType) {
      setError("من فضلك اختر نوع الإجازة");
      return;
    }

    const isHalfDay = durationMode === "half";
    const leaveDays = isHalfDay ? 0.5 : Number(days);

    if (isHalfDay) {
      if (!halfDayPeriod) {
        setError("من فضلك حدد نص الإجازة: أول الشيفت أو آخره");
        return;
      }
    } else if (!days || leaveDays < 1 || !Number.isInteger(leaveDays)) {
      setError("من فضلك أدخل عدد أيام صحيح");
      return;
    }

    if (leaveType === "unpaid" && leaveDays > UNPAID_ANNUAL_LIMIT) {
      setError(
        `حد إجازة على الحساب الخاص هو ${UNPAID_ANNUAL_LIMIT} يوم فقط في السنة.`,
      );
      return;
    }

    if (!startDate) {
      setError("من فضلك أدخل تاريخ بداية الإجازة");
      return;
    }

    if (leaveType === "annual" && !isAnnualLeaveNoticeSatisfied(startDate)) {
      setError(ANNUAL_LEAVE_NOTICE_MESSAGE);
      return;
    }

    const weeklyRestConflict = findWeeklyRestConflictInLeaveRange({
      startDate,
      days: leaveDays,
      weeklyRestDay: user?.weeklyRestDay,
    });
    if (weeklyRestConflict) {
      setError(weeklyRestConflictMessage(weeklyRestConflict));
      return;
    }

    const leaveTypeMap = {
      annual: "ANNUAL",
      sick: "SICK",
      casual: "CASUAL",
      unpaid: "UNPAID",
      compensatory: "COMPENSATORY",
      other: "UNPAID",
    };

    if (isCompensatoryLeave) {
      const now = new Date();
      const y = Number(startDate.slice(0, 4));
      const m = Number(startDate.slice(5, 7));
      if (y !== now.getFullYear() || m !== now.getMonth() + 1) {
        setError(
          "الإجازة التعويضية تُصرف في نفس الشهر الميلادي فقط ولا تُرحَّل",
        );
        return;
      }

      const requiredHours = compensatoryHoursForLeaveDays(leaveDays);
      if (compensatoryBalance && remainingCompensatoryHours <= 0) {
        setError(
          "لا يوجد رصيد إجازة تعويضية متاح هذا الشهر (أو انتهت صلاحيته).",
        );
        return;
      }
      if (
        compensatoryBalance &&
        requiredHours > remainingCompensatoryHours
      ) {
        setError(
          `رصيد الإجازة التعويضية غير كافٍ (المتبقي ${remainingCompensatoryHours} ساعة — المطلوب ${requiredHours} ساعة).`,
        );
        return;
      }
    }

    const endDate = isHalfDay
      ? startDate
      : (() => {
          const end = new Date(
            Date.UTC(
              Number(startDate.slice(0, 4)),
              Number(startDate.slice(5, 7)) - 1,
              Number(startDate.slice(8, 10)),
            ),
          );
          end.setUTCDate(end.getUTCDate() + leaveDays - 1);
          return end.toISOString().slice(0, 10);
        })();

    const overlappingLeave = findOverlappingLeaveRequest(
      pendingRequests,
      {
        startDate,
        endDate,
        days: leaveDays,
        halfDayPeriod: isHalfDay ? halfDayPeriod : null,
      },
      isOwnRequest,
    );
    if (overlappingLeave) {
      setError(OVERLAPPING_LEAVE_MESSAGE);
      return;
    }

    const coveringPermit = findPermitCoveringLeaveRange(
      pendingRequests,
      {
        startDate,
        endDate,
        days: leaveDays,
        halfDayPeriod: isHalfDay ? halfDayPeriod : null,
      },
      isOwnRequest,
    );
    if (coveringPermit) {
      setError(LEAVE_ON_PERMIT_MESSAGE);
      return;
    }

    setSubmitting(true);
    try {
      const created = await createLeaveRequest({
        leaveType: leaveTypeMap[leaveType] || "UNPAID",
        startDate,
        endDate,
        days: leaveDays,
        ...(isHalfDay ? { halfDayPeriod } : {}),
        reason: reason || "طلب إجازة",
      });

      await uploadAfterCreate("leave", created?.id);

      await addPendingRequest();

      const leaveTypeLabel =
        [...leaveTypes, ...permitTypes].find((t) => t.value === leaveType)
          ?.label || leaveType;

      addRecentActivity({
        id: Date.now(),
        text: `طلب ${leaveTypeLabel} من ${user?.name || "موظف"}`,
        date: new Date().toLocaleDateString("ar-EG"),
        type: "leave",
      });

      setSuccess(true);
    } catch (err) {
      const message = err.response?.data?.message;
      setError(
        Array.isArray(message)
          ? message.join("\n")
          : message || "تعذر إرسال طلب الإجازة",
      );
    } finally {
      setSubmitting(false);
    }
  }

  function resetForm() {
    setLeaveType("");
    setDays("");
    setDurationMode("full");
    setHalfDayPeriod("");
    setPermitStartTime("");
    setPermitEndTime("");
    setStartDate("");
    setReason("");
    setFiles([]);
    setSuccess(false);
    setError("");
  }

  async function handleCancelPending(req) {
    if (!req?.id) return;
    const isPendingPermit =
      req._source === "permit" ||
      req.category === "permit" ||
      req.type === "إذن" ||
      req.type === "إذن تعويضي";
    const label = isPendingPermit ? "الإذن" : "الإجازة";
    if (
      !window.confirm(
        `هل تريد إلغاء طلب ${label} الحالي؟ يمكنك تقديم طلب جديد بعد الإلغاء.`,
      )
    ) {
      return;
    }

    setCancelling(true);
    setError("");
    try {
      if (isPendingPermit) {
        await cancelPermit(req.id);
      } else {
        await cancelLeave(req.id);
      }
      await addPendingRequest();
      setSuccess(false);
      setLeaveType("");
    } catch (err) {
      const message = err.response?.data?.message;
      setError(
        Array.isArray(message)
          ? message.join("\n")
          : message || "تعذر إلغاء الطلب",
      );
    } finally {
      setCancelling(false);
    }
  }

  // ✅ شاشة عرض الطلب المعلق (لو موجود لنفس نوع الطلب)
  const blockingPending = isPermit
    ? existingPendingPermit
    : leaveType
      ? existingPendingLeave
      : null;

  if (blockingPending) {
    const isPendingPermit =
      blockingPending._source === "permit" ||
      blockingPending.category === "permit" ||
      blockingPending.type === "إذن" ||
      blockingPending.type === "إذن تعويضي";

    return (
      <div className="leave-form-card" dir="rtl">
        <div className="leave-pending-box">
          <div className="leave-pending-icon">⏳</div>
          <p className="leave-pending-title">
            لديك طلب{" "}
            {isPendingPermit
              ? blockingPending.type === "إذن تعويضي"
                ? "إذن تعويضي"
                : "إذن"
              : "إجازة"}{" "}
            قيد المراجعة
          </p>
          <p className="leave-pending-subtitle">
            لا يمكن تقديم طلب جديد قبل الرد على طلبك الحالي — أو يمكنك إلغاءه
          </p>

          <div className="leave-pending-details">
            <div className="leave-pending-row">
              <span className="leave-pending-label">نوع الطلب:</span>
              <span className="leave-pending-value">
                {blockingPending.type}
              </span>
            </div>
            {isPendingPermit ? (
              <>
                <div className="leave-pending-row">
                  <span className="leave-pending-label">وقت الإذن:</span>
                  <span className="leave-pending-value">
                    من {blockingPending.permitStartTime} إلى{" "}
                    {blockingPending.permitEndTime}
                  </span>
                </div>
                <div className="leave-pending-row">
                  <span className="leave-pending-label">عدد الساعات:</span>
                  <span className="leave-pending-value">
                    {blockingPending.hours} ساعة
                  </span>
                </div>
                {Number(blockingPending.deductedHours) > 0 && (
                  <div className="leave-pending-row">
                    <span className="leave-pending-label">ساعات الخصم:</span>
                    <span className="leave-pending-value">
                      {blockingPending.deductedHours} ساعة
                    </span>
                  </div>
                )}
              </>
            ) : (
              <>
                <div className="leave-pending-row">
                  <span className="leave-pending-label">عدد الأيام:</span>
                  <span className="leave-pending-value">
                    {Number(blockingPending.days) === 0.5
                      ? "نص يوم"
                      : `${blockingPending.days} أيام`}
                  </span>
                </div>
                {blockingPending.halfDayPeriodLabel ? (
                  <div className="leave-pending-row">
                    <span className="leave-pending-label">نص الشيفت:</span>
                    <span className="leave-pending-value">
                      {blockingPending.halfDayPeriodLabel}
                    </span>
                  </div>
                ) : null}
              </>
            )}
            <div className="leave-pending-row">
              <span className="leave-pending-label">تاريخ التقديم:</span>
              <span className="leave-pending-value">
                {blockingPending.submittedAt}
              </span>
            </div>
            <div className="leave-pending-row">
              <span className="leave-pending-label">الحالة:</span>
              <span className="leave-pending-status">
                {getRequestStatusLabel(blockingPending.status)}
              </span>
            </div>
          </div>

          <RequestTransparency request={blockingPending} />

          <div className="leave-pending-note">
            {user?.role === "hr"
              ? "طلبك بانتظار موافقة المدير العام"
              : user?.role === "hr-staff"
                ? "طلبك بانتظار موافقة حساب الموارد البشرية"
                : "طلبك بانتظار موافقة المدير حسب التدرج الوظيفي"}
          </div>

          {error && <p className="leave-error">{error}</p>}

          <div className="leave-pending-actions">
            <button
              type="button"
              className="leave-cancel-btn"
              disabled={cancelling}
              onClick={() => handleCancelPending(blockingPending)}
            >
              {cancelling ? "جاري الإلغاء..." : "إلغاء الطلب"}
            </button>
            <button
              type="button"
              className="leave-secondary-btn"
              onClick={resetForm}
              disabled={cancelling}
            >
              العودة للقائمة الرئيسية
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ✅ شاشة النجاح المشتركة
  if (success) {
    return (
      <div className="leave-form-card">
        <div className="leave-success-box">
          <p className="leave-success-title">تم إرسال طلبك بنجاح</p>
          <p className="leave-success-subtitle">
            تم إرسال طلب الإجازة إلى المدير المباشر، وسيتم إشعارك بالرد عند
            المراجعة.
          </p>
          <button className="leave-secondary-btn" onClick={resetForm}>
            العودة للقائمة الرئيسية
          </button>
        </div>
      </div>
    );
  }

  // ✅ فورم الإجازة / الإذن
  return (
    <form className="leave-form-card" onSubmit={handleSubmitLeave} dir="rtl">
      <h3 className="leave-section-title">تقديم طلب إجازة أو إذن</h3>

      {lastRejectedLeaveOrPermit ? (
        <div className="leave-rejected-box">
          <p className="leave-pending-title">آخر طلب مرفوض</p>
          <RequestTransparency
            request={lastRejectedLeaveOrPermit}
            compact
            onResubmit={(req) => applyLeaveDraft(buildResubmitDraft(req))}
          />
        </div>
      ) : null}

      <div className="leave-field">
        <label className="leave-label">نوع الطلب</label>
        <select
          className="leave-select"
          value={leaveType}
          onChange={(e) => handleLeaveTypeChange(e.target.value)}
        >
          <option value="">اختر نوع الإجازة أو الإذن</option>
          <optgroup label="الإجازات">
            {leaveTypes.map((t) => (
              <option key={t.value} value={t.value}>
                {t.label}
              </option>
            ))}
          </optgroup>
          <optgroup label="الأذونات">
            {permitTypes.map((t) => (
              <option key={t.value} value={t.value}>
                {t.label}
              </option>
            ))}
          </optgroup>
        </select>
      </div>

      {isPermit && lateDays.length > 0 && (
        <div className="leave-field">
          <label className="leave-label">أيام تأخير مقترحة هذا الشهر</label>
          <div className="leave-late-chips" role="group" aria-label="أيام التأخير">
            {lateDays.slice(0, 8).map((day) => (
              <button
                key={day.date}
                type="button"
                className={
                  startDate === day.date
                    ? "leave-late-chip leave-late-chip-active"
                    : "leave-late-chip"
                }
                onClick={() => setStartDate(day.date)}
              >
                {formatLateDayChipLabel(day)}
              </button>
            ))}
          </div>
          <p className="leave-late-chips-hint">
            اختر يوم التأخير الفعلي لتجنب تقديم الإذن على تاريخ خاطئ
          </p>
        </div>
      )}

      {isLeaveRequestType && absentDays.length > 0 && (
        <div className="leave-field">
          <label className="leave-label">أيام غياب مقترحة هذا الشهر</label>
          <div className="leave-late-chips" role="group" aria-label="أيام الغياب">
            {absentDays.slice(0, 8).map((day) => (
              <button
                key={day.date}
                type="button"
                className={
                  startDate === day.date
                    ? "leave-late-chip leave-late-chip-active"
                    : "leave-late-chip"
                }
                onClick={() => {
                  setStartDate(day.date);
                  if (durationMode === "full" && !days) {
                    setDays("1");
                  }
                }}
              >
                {formatAbsentDayChipLabel(day)}
              </button>
            ))}
          </div>
          <p className="leave-late-chips-hint">
            اختر يوم الغياب الفعلي لتجنب تقديم الإجازة على تاريخ خاطئ
          </p>
        </div>
      )}

      <div className="leave-row">
        <div className="leave-field">
          <label className="leave-label">
            {isPermit ? "تاريخ الإذن" : "تاريخ البداية"}
          </label>
          <input
            type="date"
            className="leave-input"
            value={startDate}
            min={
              leaveType === "annual"
                ? earliestAnnualLeaveStartDate()
                : undefined
            }
            onChange={(e) => setStartDate(e.target.value)}
          />
        </div>

        {!isPermit && (
          <div className="leave-field">
            <label className="leave-label">مدة الإجازة</label>
            <select
              className="leave-input"
              value={durationMode}
              onChange={(e) => {
                const mode = e.target.value;
                setDurationMode(mode);
                if (mode === "half") {
                  setDays("0.5");
                } else {
                  setDays("");
                  setHalfDayPeriod("");
                }
              }}
            >
              <option value="full">يوم كامل / أيام</option>
              <option value="half">نص يوم</option>
            </select>
          </div>
        )}
      </div>

      {isPermit && startDate && !attendanceHintsLoading && (
        selectedLateDay ? (
          <div className="leave-hint" role="status">
            يوجد تأخير مسجّل في هذا التاريخ ({selectedLateDay.lateMinutes} دقيقة).
            الإذن سيغطي يوم التأخير الصحيح بعد الاعتماد.
          </div>
        ) : (
          <div className="leave-warning" role="status">
            لا يوجد تأخير مسجّل في تاريخ الإذن المختار.
            لو الإذن لتغطية تأخير، راجع التاريخ — التقديم على يوم غلط لن يلغي جزاء التأخير الفعلي.
            {lateDays.length > 0
              ? " يمكنك اختيار أحد أيام التأخير المقترحة أعلاه."
              : " يمكنك المتابعة إذا كان الإذن لخروج أثناء اليوم وليس لتأخير."}
          </div>
        )
      )}

      {isLeaveRequestType && startDate && !attendanceHintsLoading && selectedAbsentDay && (
        <div className="leave-hint" role="status">
          يوجد غياب مسجّل في هذا التاريخ. الإجازة ستغطي يوم الغياب بعد الاعتماد
          (حسب اعتماد المدير والموارد البشرية).
        </div>
      )}

      {showLeaveAbsenceWarning && (
        <div className="leave-warning" role="status">
          لا يوجد غياب مسجّل في تاريخ بداية الإجازة.
          لو الإجازة لتغطية غياب، راجع التاريخ — التقديم على يوم غلط لن يلغي جزاء الغياب الفعلي.
          {absentDays.length > 0
            ? " يمكنك اختيار أحد أيام الغياب المقترحة أعلاه."
            : " يمكنك المتابعة إذا كانت إجازة مخططة مسبقاً وليست لتغطية غياب."}
        </div>
      )}

      {leaveWeeklyRestConflict ? (
        <div className="leave-warning" role="status">
          {weeklyRestConflictMessage(leaveWeeklyRestConflict)}. يوم راحتك
          الأسبوعية هو {leaveWeeklyRestConflict.label || weeklyRestDayLabelAr(user?.weeklyRestDay)}.
        </div>
      ) : null}

      {!isPermit && durationMode === "full" && (
        <div className="leave-row">
          <div className="leave-field">
            <label className="leave-label">عدد الأيام</label>
            <input
              type="number"
              min="1"
              max={leaveType === "unpaid" ? String(UNPAID_ANNUAL_LIMIT) : "10"}
              className="leave-input"
              placeholder="مثال: 3"
              value={days}
              onChange={(e) => setDays(e.target.value)}
            />
          </div>
        </div>
      )}

      {!isPermit && durationMode === "half" && (
        <div className="leave-row">
          <div className="leave-field">
            <label className="leave-label">نص الشيفت</label>
            <select
              className="leave-input"
              value={halfDayPeriod}
              onChange={(e) => setHalfDayPeriod(e.target.value)}
            >
              <option value="">اختر نصف الإجازة</option>
              <option value="FIRST_HALF">أول الشيفت</option>
              <option value="SECOND_HALF">آخر الشيفت</option>
            </select>
          </div>
        </div>
      )}

      {/* ✅ حقول الوقت الخاصة بالإذن */}
      {isPermit && (
        <>
          <div className="leave-row">
            <div className="leave-field">
              <label className="leave-label">وقت البداية</label>
              <input
                type="time"
                className="leave-input"
                value={permitStartTime}
                onChange={(e) => {
                  setPermitStartTime(e.target.value);
                }}
              />
            </div>
            <div className="leave-field">
              <label className="leave-label">وقت النهاية</label>
              <input
                type="time"
                className="leave-input"
                value={permitEndTime}
                onChange={(e) => setPermitEndTime(e.target.value)}
              />
            </div>
          </div>

          {/* عرض عدد الساعات المحسوبة تلقائياً */}
          {computedHours !== null && computedHours > 0 && (
            <div className="leave-hours-preview">
              <span className="leave-hours-preview-icon">🕐</span>
              <span>
                مدة الإذن:{" "}
                <strong>
                  {computedHours % 1 === 0
                    ? `${computedHours} ساعة`
                    : `${computedHours} ساعة (${computedHours * 60} دقيقة)`}
                </strong>
                {permitEndTime < permitStartTime ? " (يعبر منتصف الليل)" : ""}
                {isCompensatory ? (
                  <> — بدون خصم من الراتب (من رصيد الساعات الزيادة)</>
                ) : deductedHours > 0 ? (
                  <>
                    {" "}
                    — خصم الراتب:{" "}
                    <strong>{deductedHours} ساعة</strong>
                  </>
                ) : (
                  <> — بدون خصم (ضمن الساعتين المجانيتين شهرياً)</>
                )}
              </span>
            </div>
          )}

          {isCompensatory && compensatoryBalance && (
            <div className="leave-hours-preview">
              <span className="leave-hours-preview-icon">📊</span>
              <span>
                رصيد تعويضي هذا الشهر: مكتسب{" "}
                <strong>{compensatoryBalance.earned}</strong> — مصروف{" "}
                <strong>{compensatoryBalance.used}</strong> — متبقي{" "}
                <strong>{remainingCompensatoryHours}</strong> ساعة
              </span>
            </div>
          )}

          {isCompensatory &&
            compensatoryBalance &&
            remainingCompensatoryHours <= 0 && (
              <div className="leave-error" role="alert">
                لا يوجد رصيد إذن تعويضي متاح هذا الشهر.
              </div>
            )}

          {!isCompensatory && permitBalance && (
            <div className="leave-hours-preview">
              <span className="leave-hours-preview-icon">📊</span>
              <span>
                رصيد الشهر: مستخدم{" "}
                <strong>{usedPermitHours}</strong> من{" "}
                <strong>{permitMaxMonthlyHours}</strong> ساعة — متبقي{" "}
                <strong>{remainingPermitHours}</strong> ساعة
                {Number(permitBalance.freeRemaining) > 0 && (
                  <> (منها {permitBalance.freeRemaining} مجانية)</>
                )}
              </span>
            </div>
          )}

          {!isCompensatory &&
            permitBalance &&
            remainingPermitHours <= 0 && (
            <div className="leave-error" role="alert">
              تم استنفاد رصيد الأذونات لهذا الشهر. لا يمكن تقديم إذن جديد،
              وسيتم الخصم مباشرة من الراتب في حالة التأخير.
            </div>
          )}

          <div className="leave-permit-note">
            {isCompensatory
              ? "ملحوظة: الإذن التعويضي من رصيد ساعات الزيادة بعد تحويل الموارد البشرية — بدون خصم من الراتب، ويُصرف في نفس الشهر الميلادي فقط ولا يُرحَّل. المدة بنصف ساعة أو مضاعفاتها."
              : `ملحوظة: رصيد شهري — أول ${formatPermitHoursAr(permitFreeHours)} بدون خصم، ثم ${formatPermitHoursAr(permitPaidCapHours)} كل ساعة تُخصم ${formatPermitDeductionMultiplierAr(permitDeductionMultiplier)} من الراتب. بعد ${permitMaxMonthlyHours} ساعات يُمنع تقديم أذونات جديدة ويُخصم التأخير مباشرة من الراتب. المدة بنصف ساعة أو مضاعفاتها (0.5، 1، 1.5، …). بعد موافقة مدير الفرع يُحال الطلب مباشرة إلى الموارد البشرية.`}
          </div>
        </>
      )}

      {!isPermit && leaveType && (
        <>
          {leaveType === "unpaid" && (
            <div className="leave-hint" role="note">
              إجازة على الحساب الخاص: تُخصم من الراتب يوم بيوم، والحد الأقصى{" "}
              {UNPAID_ANNUAL_LIMIT} يوم في السنة الميلادية بدون ترحيل، ومتاحة
              بدون انتظار 6 أشهر من التعيين.
            </div>
          )}
          {isCompensatoryLeave && (
            <div className="leave-hint" role="note">
              إجازة تعويضية من رصيد الساعات الإضافية: 8 ساعات = يوم كامل، 4
              ساعات = نصف يوم بدون خصم من الراتب. الرصيد صالح لنفس الشهر فقط
              ولا يُرحَّل.
            </div>
          )}
          {isCompensatoryLeave && compensatoryBalance && (
            <div className="leave-hours-preview">
              <span className="leave-hours-preview-icon">📊</span>
              <span>
                رصيد تعويضي هذا الشهر: مكتسب{" "}
                <strong>{compensatoryBalance.earned}</strong> — مصروف{" "}
                <strong>{compensatoryBalance.used}</strong> — متبقي{" "}
                <strong>{remainingCompensatoryHours}</strong> ساعة
                {requestedCompensatoryLeaveHours > 0 && (
                  <>
                    {" "}
                    — المطلوب لهذا الطلب:{" "}
                    <strong>{requestedCompensatoryLeaveHours}</strong> ساعة
                  </>
                )}
              </span>
            </div>
          )}
          {leaveType === "annual" && (
            <div className="leave-hint" role="note">
              يجب تقديم الإجازة الاعتيادية قبل تاريخ بدايتها بـ 3 أيام على
              الأقل.
            </div>
          )}
          {(leaveType === "annual" || leaveType === "casual") && (
            <div className="leave-hint" role="note">
              الإجازة الاعتيادية والعارضة متاحتان بعد مرور 6 أشهر من تاريخ
              التعيين، ويتجددان في 1 يناير بدون ترحيل رصيد العام السابق.
            </div>
          )}
          <div className="leave-field">
            <label className="leave-label">سبب الإجازة (اختياري)</label>
            <textarea
              className="leave-textarea"
              rows={3}
              placeholder="اكتب تفاصيل إضافية إن وجدت"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
            />
          </div>

          <div className="leave-field">
            <AttachmentUploader
              entityType={isPermit ? "permission" : "leave"}
              files={files}
              onFilesChange={setFiles}
              disabled={submitting}
            />
            {createdEntity && (
              <AttachmentList
                entityType={createdEntity.type}
                entityId={createdEntity.id}
                refreshKey={listRefresh}
              />
            )}
          </div>
        </>
      )}

      {isPermit && (
        <>
          <div className="leave-field">
            <label className="leave-label">سبب الإذن (اختياري)</label>
            <textarea
              className="leave-textarea"
              rows={3}
              placeholder="اكتب سبب الإذن إن وجد"
              maxLength={500}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
            />
          </div>
          <div className="leave-field">
            <AttachmentUploader
              entityType="permission"
              files={files}
              onFilesChange={setFiles}
              disabled={submitting}
            />
            {createdEntity?.type === "permission" && (
              <AttachmentList
                entityType="permission"
                entityId={createdEntity.id}
                refreshKey={listRefresh}
              />
            )}
          </div>
        </>
      )}

      {error && <p className="leave-error">{error}</p>}

      <div className="leave-footer-note">
        {isCompensatoryLeave
          ? "الإجازة التعويضية بدون خصم من الراتب — 8 ساعات = يوم كامل، 4 ساعات = نصف يوم — صالحة لنفس الشهر فقط."
          : isCompensatoryPermit
            ? "الإذن التعويضي بدون خصم من الراتب — صالح لنفس الشهر فقط."
            : isPermit
              ? `رصيد شهري ${permitMaxMonthlyHours} ساعات: ${formatPermitHoursAr(permitFreeHours)} مجاناً ثم ${formatPermitHoursAr(permitPaidCapHours)} بخصم ${formatPermitDeductionMultiplierAr(permitDeductionMultiplier)}. بعد الاستنفاد يُخصم التأخير مباشرة من الراتب.`
              : "سيتم إرسال هذا الطلب حسب التدرج الوظيفي للمراجعة والموافقة."}
      </div>

      <div className="leave-buttons-row">
        <button type="button" className="leave-back-btn" onClick={resetForm}>
          ← رجوع
        </button>
        <button
          type="submit"
          className="leave-submit-btn"
          disabled={
            submitting ||
            (isCompensatoryLeave &&
              compensatoryBalance &&
              (remainingCompensatoryHours <= 0 ||
                requestedCompensatoryLeaveHours > remainingCompensatoryHours)) ||
            (isCompensatoryPermit &&
              compensatoryBalance &&
              remainingCompensatoryHours <= 0) ||
            (isPermit &&
              !isCompensatoryPermit &&
              permitBalance &&
              remainingPermitHours <= 0)
          }
        >
          {submitting ? "جاري الإرسال..." : "إرسال الطلب"}
        </button>
      </div>
    </form>
  );
}

export default LeaveRequestForm;
